import {
  Box,
  Button,
  ButtonGroup,
  Column,
  Dialog,
  Row,
  Text,
  TextWithTooltip,
  useToast,
} from "@hightouch/htui";

import {
  type ExportContentEmailToNativeDeliveryMutation,
  useContentEmailQuery,
  useContentEmailVersionQuery,
  useExportContentEmailToNativeDeliveryMutation,
} from "src/graphql";
import { useOpenInNewTab } from "src/hooks/use-open-in-new-tab";
import { versionNumber } from "src/pages/lifecycle-studio/components/version-number";
import { EmailThumbnail } from "./email-thumbnail";

/** Any campaign link disables creating a standalone campaign, which would bypass the linked one. */
export const ND_LINKED_CAMPAIGN_REASON =
  "This message sends through its Native Delivery campaign";

type ExportProblem =
  ExportContentEmailToNativeDeliveryMutation["exportContentEmailToNativeDelivery"]["problems"][number];

type Props = {
  isOpen: boolean;
  onClose: () => void;
  contentEmailId: string;
  contentEmailVersionId: string;
};

/** Copies one saved version of an email into a new draft Native Delivery campaign, and opens it in a new tab. */
export function CreateCampaignDialog({
  isOpen,
  onClose,
  contentEmailId,
  contentEmailVersionId,
}: Props) {
  const openInNewTab = useOpenInNewTab();
  const { toast } = useToast();
  const exportMutation = useExportContentEmailToNativeDeliveryMutation();
  const { data: email, isLoading: isLoadingEmail } = useContentEmailQuery(
    { id: contentEmailId },
    { select: (data) => data.content_emails_by_pk },
  );
  const { data: html, isLoading: isLoadingHtml } = useContentEmailVersionQuery(
    { id: contentEmailVersionId },
    {
      select: (data) => data.content_email_versions_by_pk?.resolved_html?.html,
    },
  );
  const result = exportMutation.data?.exportContentEmailToNativeDelivery;
  const problems = result && result.campaignId == null ? result.problems : null;
  const number = versionNumber(
    email?.content_email_versions ?? [],
    contentEmailVersionId,
  );
  const name = email?.name || "Untitled email";

  const close = () => {
    exportMutation.reset();
    onClose();
  };

  const create = async () => {
    try {
      const { exportContentEmailToNativeDelivery: created } =
        await exportMutation.mutateAsync({
          contentEmailId,
          contentEmailVersionId,
        });
      if (created.campaignId == null) return;
      toast({
        id: "create-campaign",
        title: "Campaign created",
        variant: "success",
      });
      close();
      openInNewTab(`/native-delivery/campaigns/${created.campaignId}`);
    } catch (err) {
      toast({
        id: "create-campaign",
        title: "Failed to create campaign",
        message: err instanceof Error ? err.message : "Something went wrong.",
        variant: "error",
      });
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      // Says Native Delivery until the nav consolidation renames it to Lifecycle Studio.
      title="Export to Native Delivery"
      variant="form"
      onClose={exportMutation.isLoading ? () => undefined : close}
      actions={
        problems === null ? (
          <ButtonGroup>
            <Button onClick={close} isDisabled={exportMutation.isLoading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              isLoading={exportMutation.isLoading}
              onClick={create}
            >
              {/* Says Native Delivery until the nav consolidation renames it to Lifecycle Studio. */}
              Export
            </Button>
          </ButtonGroup>
        ) : (
          <Button onClick={close}>Close</Button>
        )
      }
    >
      {problems === null ? (
        <Column gap={4}>
          <Column gap={2}>
            <Text fontWeight="medium">Email</Text>
            <Row
              align="center"
              gap={3}
              p={3}
              borderWidth="hairline"
              borderStyle="solid"
              borderColor="base.border"
              borderRadius="lg"
            >
              <EmailThumbnail
                html={html}
                name={name}
                size="md"
                isLoading={isLoadingHtml}
              />
              <Column minWidth={0}>
                <TextWithTooltip fontWeight="medium">
                  {isLoadingEmail ? "" : name}
                </TextWithTooltip>
                <Text color="text.secondary">
                  {number === undefined ? "Email" : `Email · v${number}`}
                </Text>
              </Column>
            </Row>
          </Column>
          <Text color="text.secondary">
            {/* Says Native Delivery until the new nav renames it Lifecycle Studio. */}
            Your campaign will be saved as a draft in Native Delivery. You can
            set your audience, schedule, and send limits before activating it.
          </Text>
        </Column>
      ) : (
        <Column gap={2}>
          <Text>
            This email can't be sent with Hightouch yet. Fix these problems in
            Lifecycle Studio, then try again.
          </Text>
          <Box as="ul" ml={4}>
            {problems.map((problem, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: problems have no id and never reorder
              <Box as="li" key={index}>
                <Text>{problemLine(problem)}</Text>
              </Box>
            ))}
          </Box>
        </Column>
      )}
    </Dialog>
  );
}

function problemLine(problem: ExportProblem): string {
  const where = [problem.blockName, problem.variantName]
    .filter((part) => part != null)
    .join(", ");
  return where === "" ? problem.message : `${where}: ${problem.message}`;
}
